import * as CL from '@design-system/component-library';
import { useState } from 'react';
import { Link, generatePath } from 'react-router';
import { dsClass } from '../../common/constants/dsClasses.js';
import { mdmIdQueryStr, paths } from '../../common/constants/pathVariables.js';
import { OrderConfirmationListFields } from '../../common/enums.js';
import { downloadOrderConfirmationPdf } from '../../common/fetch.js';
import { useSearchParams } from '../../common/hooks/useSearchParams.js';
import {
  companyMsg,
  contentMsg,
  downloadOrderConfirmationMsg,
  noOrdersMsg,
  orderConfirmationNotAvailableMsg,
  orderDateMsg,
  orderMsg,
  t,
  technicalErrorMsg,
  userMsg,
} from '../../common/i18n';
import { getCompanyName } from '../../common/utils/accountUtils.js';
import { formatTimeStampToHHmm, formatTimestampToDDMMYYYY } from '../../common/utils/dateUtils.js';
import { type DownloadError, downloadFile } from '../../common/utils/domUtils.js';
import { truncateText } from '../../common/utils/stringUtils.js';
import { ConsolidatedListView } from '../ConsolidatedListView/ConsolidatedListView.js';
import { Highlight } from '../Highlight/Highlight.js';
import {
  DEFAULT_ITEMS_PER_PAGE,
  type DefaultListSearchParams,
  ListPagination,
  ListSearch,
  Table,
  TableDescription,
} from '../Table';
import { setDefaultItemsPerPage } from '../Table/tableUtils.js';
import type { CLTableColumn } from '../../common/types/componentLibrary';
import type { CustomerOrderSearchResponse } from '../../generated/api/models.js';
import type { AuthenticatedUser } from '../../public/site/authState';
import type { JSX } from 'react';

const truncateTextMaxChars = 100;

export interface CustomerOrderListWithTableProps {
  customerOrders?: CustomerOrderSearchResponse[];
  totalItems: number;
  searchQuery?: string;
  authenticatedUser?: AuthenticatedUser;
}

const generateCustomerOrderUrl = (path: string, order: CustomerOrderSearchResponse) => {
  const mdmIdStr = mdmIdQueryStr(path, order.result.accountMasterId);
  return `${generatePath(path, { orderId: order?.result?.customerOrderDisplayId })}${mdmIdStr}`;
};

export const OrderConfirmationList = ({
  customerOrders,
  totalItems,
  searchQuery,
  authenticatedUser,
}: CustomerOrderListWithTableProps): JSX.Element => {
  const searchTerms = searchQuery?.split(' ') || [];
  const searchParams = useSearchParams<DefaultListSearchParams>();
  const [downloadError, setDownloadError] = useState<DownloadError>(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const downloadOrderConfirmation = async (customerOrderDisplayId: string) => {
    setDownloadError(false);
    setDownloadingId(customerOrderDisplayId);
    const error = await downloadFile(
      () => downloadOrderConfirmationPdf(customerOrderDisplayId),
      `order-confirmation-${customerOrderDisplayId}.pdf`
    );
    setDownloadingId(null);
    setDownloadError(error);
  };

  const columns: CLTableColumn[] = [
    {
      key: OrderConfirmationListFields.CUSTOMER_ORDER_DISPLAY_ID,
      label: t.C001(orderMsg),
      sortable: true,
      width: '15%',
    },
    {
      key: OrderConfirmationListFields.CREATED,
      label: t.ZTPC(orderDateMsg),
      sortable: true,
      width: '10%',
    },
    {
      key: OrderConfirmationListFields.DELIVERY_ORDERS,
      label: t.NBNH(contentMsg),
      width: '25%',
    },
    {
      key: OrderConfirmationListFields.CUSTOMER_ORDER_CONTACT,
      label: t.XRDP(userMsg),
      width: '15%',
    },
    {
      key: OrderConfirmationListFields.COMPANY,
      label: t.KJTS(companyMsg),
      width: '10%',
    },
    {
      key: OrderConfirmationListFields.DOWNLOAD_ORDER_CONFIRMATION,
    },
  ];

  const rows = customerOrders?.map(value => ({
    [OrderConfirmationListFields.CUSTOMER_ORDER_DISPLAY_ID]: (
      <TableDescription
        description={t.C001(orderMsg)}
        title={
          <Link to={generateCustomerOrderUrl(paths.CUSTOMER_ORDER, value)}>
            <Highlight text={value.result.customerOrderDisplayId ?? ''} highlights={searchTerms} />
          </Link>
        }
      />
    ),
    [OrderConfirmationListFields.CREATED]: (
      <TableDescription
        description={value.result.created ? formatTimeStampToHHmm(value.result.created) : ''}
        title={formatTimestampToDDMMYYYY(value.result.created)}
      />
    ),
    [OrderConfirmationListFields.DELIVERY_ORDERS]: (
      <Highlight text={truncateText(value.result.deliveryOrders, truncateTextMaxChars)} highlights={searchTerms} />
    ),
    [OrderConfirmationListFields.CUSTOMER_ORDER_CONTACT]: (
      <TableDescription
        description={
          <Highlight
            text={truncateText(value.result.customerOrderContactEmail || '', truncateTextMaxChars)}
            highlights={searchTerms}
          />
        }
        title={
          <Highlight
            text={truncateText(value.result.customerOrderContactName || '', truncateTextMaxChars)}
            highlights={searchTerms}
          />
        }
      />
    ),
    [OrderConfirmationListFields.COMPANY]: getCompanyName(authenticatedUser, value.result.accountMasterId),
    [OrderConfirmationListFields.DOWNLOAD_ORDER_CONFIRMATION]: value.result.customerOrderDisplayId && (
      <CL.Button
        className={`${dsClass.DISPLAY_FLEX} ${dsClass.ALIGN_ITEMS_CENTER} ${dsClass.PADDING_LEFT_0}  ${dsClass.BACKGROUND_COLOR_TRANSPARENT} ${dsClass.BORDER_STYLE_NONE}`}
        color="link"
        size="s"
        loading={downloadingId === value.result.customerOrderDisplayId}
        icon={<CL.Icon className={`${dsClass.MARGIN_RIGHT_2}`} icon="download" type="filled" />}
        onClick={() => downloadOrderConfirmation(value.result.customerOrderDisplayId!)}
      >
        {t.FHKH(downloadOrderConfirmationMsg)}
      </CL.Button>
    ),
  }));

  return (
    <div className={`of-order-confirmation-list ${dsClass.MARGIN_TOP_4}`}>
      {downloadError && (
        <CL.Notification
          type="error"
          text={downloadError === 'notFound' ? t.UJGD(orderConfirmationNotAvailableMsg) : t.T4OU(technicalErrorMsg)}
          visible={true}
        />
      )}
      <ConsolidatedListView
        authenticatedUser={authenticatedUser}
        listElement={<Table columns={columns} noItemsText={t.QFXU(noOrdersMsg)} rows={rows || []} />}
        listPagination={<ListPagination totalItems={totalItems} />}
        listSearch={
          <ListSearch
            onSearch={() => setDefaultItemsPerPage(new URLSearchParams(searchParams), DEFAULT_ITEMS_PER_PAGE)}
          />
        }
      />
    </div>
  );
};
